import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import client, { formatErr } from "../lib/api";
import { Ticket, Copy, Plus, Trash2, Check, Zap, Shield, Share2 } from "lucide-react";

function statusPill(s) {
    const map = {
        draft: { color: "#52525b", label: "Draft" },
        armed: { color: "#39FF14", label: "Armed" },
        claiming: { color: "#F59E0B", label: "Claiming" },
        claimed: { color: "#a78bfa", label: "Claimed" },
    };
    return map[s] || { color: "#52525b", label: s };
}

export default function MyInvites() {
    const [list, setList] = useState([]);
    const [creating, setCreating] = useState(false);
    const [logBusy, setLogBusy] = useState(null);

    const load = async () => {
        const r = await client.get("/invites/me");
        setList(r.data || []);
    };
    useEffect(() => { load(); }, []);

    const createDraft = async () => {
        setCreating(true);
        try {
            const r = await client.post("/invites/draft", { note: "" });
            toast.success("Draft invite created — complete the exercises to arm it.");
            setList([r.data, ...list]);
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setCreating(false);
        }
    };

    const logProgress = async (inv, ex, count) => {
        setLogBusy(`${inv.id}-${ex.exercise_type}`);
        try {
            const r = await client.post(`/invites/${inv.id}/host-progress`, {
                exercise_type: ex.exercise_type,
                count: Number(count) || 0,
            });
            const next = list.map((i) => (i.id === inv.id ? r.data : i));
            setList(next);
            if (r.data.status === "armed") {
                toast.success("Invite ARMED — share the code now.");
            } else {
                toast.success(`+${count} ${ex.exercise_type}`);
            }
        } catch (e) {
            toast.error(formatErr(e));
        } finally {
            setLogBusy(null);
        }
    };

    const removeInvite = async (inv) => {
        if (!window.confirm("Delete this invite?")) return;
        try {
            await client.delete(`/invites/${inv.id}`);
            setList(list.filter((i) => i.id !== inv.id));
        } catch (e) {
            toast.error(formatErr(e));
        }
    };

    const copyShare = (inv) => {
        const link = `${window.location.origin}/claim/${inv.code}`;
        navigator.clipboard.writeText(link).then(
            () => toast.success("Invite link copied"),
            () => toast.error("Copy failed")
        );
    };

    return (
        <div className="px-5 pt-8 pb-28" data-testid="my-invites-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="label-cap text-xs text-zinc-500">Recruit</div>
                    <h1 className="font-display font-black text-3xl uppercase tracking-tight leading-none">My Invites</h1>
                </div>
                <button
                    onClick={createDraft}
                    disabled={creating}
                    data-testid="invite-new-btn"
                    className="btn-neon px-3 py-2 flex items-center gap-1.5 text-xs disabled:opacity-50"
                >
                    <Plus size={14} /> New
                </button>
            </div>

            <p className="text-xs text-zinc-500 mt-3 leading-relaxed">
                Earn your right to recruit. Complete the host exercises to ARM an invite, then share the link.
                Your recruit then has <span className="text-neon-orange">1 hour</span> to prove themselves before the code locks.
            </p>

            <div className="mt-6 space-y-3">
                {list.length === 0 && (
                    <div className="surface-1 p-8 text-center text-zinc-500 text-sm">
                        No invites yet. Tap <span className="text-neon-green">New</span> to start.
                    </div>
                )}
                {list.map((inv) => {
                    const pill = statusPill(inv.status);
                    return (
                        <motion.div
                            key={inv.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="surface-2 p-4"
                            data-testid={`invite-card-${inv.id}`}
                        >
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    <Ticket size={14} className="text-neon-green" />
                                    <span className="font-mono font-bold text-base tracking-widest">{inv.code}</span>
                                </div>
                                <span
                                    className="label-cap text-[9px] px-2 py-1"
                                    style={{ background: pill.color + "22", color: pill.color, border: `1px solid ${pill.color}` }}
                                    data-testid={`invite-status-${inv.id}`}
                                >
                                    {pill.label}
                                </span>
                            </div>

                            {/* Host progress block (only shown while draft) */}
                            {inv.status === "draft" && (
                                <div className="mt-3 space-y-2">
                                    <div className="label-cap text-[10px] text-zinc-500">
                                        Complete to arm:
                                    </div>
                                    {inv.host_exercises.map((ex) => (
                                        <HostExerciseRow
                                            key={ex.exercise_type}
                                            inv={inv}
                                            ex={ex}
                                            progress={(inv.host_progress || {})[ex.exercise_type] || 0}
                                            busy={logBusy === `${inv.id}-${ex.exercise_type}`}
                                            onLog={(n) => logProgress(inv, ex, n)}
                                        />
                                    ))}
                                </div>
                            )}

                            {/* Recipient progress block (only while claiming) */}
                            {inv.status === "claiming" && (
                                <div className="mt-3 surface-1 p-3 border-l-2 border-l-neon-orange">
                                    <div className="label-cap text-[10px] text-neon-orange">Recipient is claiming</div>
                                    {inv.recipient_exercises.map((ex) => (
                                        <div key={ex.exercise_type} className="flex justify-between text-xs mt-1">
                                            <span>{ex.exercise_type}</span>
                                            <span>{(inv.recipient_progress || {})[ex.exercise_type] || 0} / {ex.reps_required}</span>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {inv.status === "claimed" && (
                                <div className="mt-3 surface-1 p-3 flex items-center gap-2">
                                    <Check size={14} className="text-neon-green" />
                                    <span className="text-xs text-zinc-300">Recruit locked in — host bonus paid out.</span>
                                </div>
                            )}

                            <div className="mt-3 flex gap-2">
                                {inv.status === "armed" && (
                                    <button
                                        onClick={() => copyShare(inv)}
                                        data-testid={`invite-share-${inv.id}`}
                                        className="flex-1 btn-neon py-2 text-xs flex items-center justify-center gap-1.5"
                                    >
                                        <Share2 size={12} /> Copy Invite Link
                                    </button>
                                )}
                                {inv.status !== "claimed" && (
                                    <button
                                        onClick={() => removeInvite(inv)}
                                        data-testid={`invite-delete-${inv.id}`}
                                        className="px-3 py-2 text-xs text-zinc-400 hover:text-neon-orange"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                )}
                            </div>
                        </motion.div>
                    );
                })}
            </div>
        </div>
    );
}

function HostExerciseRow({ inv, ex, progress, busy, onLog }) {
    const [v, setV] = useState("");
    const done = progress >= ex.reps_required;
    return (
        <div className="flex items-center gap-2" data-testid={`invite-host-${inv.id}-${ex.exercise_type}`}>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-bold capitalize truncate">{ex.exercise_type}</div>
                <div className="text-[10px] text-zinc-500 uppercase tracking-wider">
                    {progress} / {ex.reps_required}
                </div>
                <div className="w-full h-1 bg-charcoal-700 mt-1 overflow-hidden">
                    <div
                        className={`h-full transition-all ${done ? "bg-neon-green" : "bg-neon-orange"}`}
                        style={{ width: `${Math.min(100, (progress / ex.reps_required) * 100)}%` }}
                    />
                </div>
            </div>
            {!done && (
                <>
                    <input
                        type="number"
                        min={1}
                        value={v}
                        placeholder="+reps"
                        onChange={(e) => setV(e.target.value)}
                        className="w-16 bg-charcoal-900 border border-charcoal-500 px-2 py-1.5 text-center text-sm"
                    />
                    <button
                        onClick={() => { if (v) { onLog(v); setV(""); } }}
                        disabled={busy || !v}
                        data-testid={`invite-log-${inv.id}-${ex.exercise_type}`}
                        className="px-3 py-1.5 text-xs label-cap bg-neon-green text-black disabled:opacity-40"
                    >
                        Log
                    </button>
                </>
            )}
            {done && <Check size={16} className="text-neon-green" />}
        </div>
    );
}
